import React, { useState } from 'react'
import moment from 'moment'
import { observer } from 'mobx-react'
import { runInAction } from 'mobx'
import { Link, CircularProgress } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import { dateFormat } from 'config/callpro.config'
import { useStores } from 'config/store'
import { CallsStore } from 'config/store/CallPro/Call/CallsStore'
import { CallDashboardTabs, CallOutcome } from 'interfaces/Call.interface'

import { LinearProgress } from '../LinearProgress'
import { ChartCard } from 'components/CallPro/ChartCard'
import CustomTooltip from '../CustomTooltip/CustomTooltip'
import { useStyles } from './CallNeedsFollowUp.style'
import CallDetailsDrawer from 'components/CallPro/SideDrawer/CallDetailsDrawer/CallDetailsDrawer'
import AllCallNeedsFollowUpDrawer from '../SideDrawer/AllCallNeedsFollowUp/AllCallNeedsFollowUpDrawer'
import EmptyBarChart from '../EmptyCharts/EmptyBarChart'

interface CallNeedsFollowUpInterface {
  isAccordionContent?: boolean
}

type CallNeedsFollowUpItem = CallsStore['followUpReason']['records'][number]

const CallNeedsFollowUp = (props: CallNeedsFollowUpInterface) => {
  const { isAccordionContent } = props
  const { callsStore, dashboardUIStore } = useStores()
  const classes = useStyles()
  const { followUpReason: _followUpReason, loadingStates } = callsStore
  const followUpReason = _followUpReason.records
  const firstRecordDate = _followUpReason.firstRecordDate

  const total = followUpReason.reduce(
    (accumulator, currentElement) => accumulator + currentElement.value,
    0
  )
  const [drawerOpen, setDrawerOpen] = useState(false)

  const handleToggleDrawer = () => setDrawerOpen((value) => !value)

  const handleItemClick = (item: CallNeedsFollowUpItem) => () => {
    runInAction(() => {
      const tab = CallDashboardTabs.ALL_CALLS
      const outcome = CallOutcome.ALL_CALLS
      dashboardUIStore.setCurrentTabbedOpened(tab)
      callsStore.setFilters({
        outcome,
        followUpReason: [item.name],
      })
      callsStore.setPaginationPage(1)
      callsStore.refreshAllCallsTab()
    })
  }

  // TOOLTIP CONTENT
  const tooltipContent = (
    <div className={classes.metricTooltip}>
      <div className={classes.tooltipTitle}>Click to see the full list</div>
    </div>
  )

  return (
    <>
      <ChartCard
        headerTitle="TOP REASONS CALL NEEDS FOLLOW UP"
        tooltipText="These are the top reasons why certain calls require follow-up by the front desk."
        headerTotalText="Total Calls"
        headerTotalValue={total}
        isAccordionContent={isAccordionContent}
      >
        {loadingStates.followUpReason ? (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
            }}
          >
            <CircularProgress />
          </div>
        ) : followUpReason.length > 0 ? (
          <>
            {followUpReason.slice(0, 5).map((reason, key) => {
              return (
                <LinearProgress
                  key={`item-${key}`}
                  value={reason.value}
                  total={total}
                  label={
                    <Link
                      component="button"
                      variant="body1"
                      underline="none"
                      color="initial"
                      onClick={handleItemClick(reason)}
                      classes={{ root: classes.link }}
                    >
                      {reason.name}
                    </Link>
                  }
                />
              )
            })}
            <div className={classes.footer}>
              <div className={classes.container}>
                <div className={classes.infoContainer}>
                  <div className={classes.info}>
                    <CustomTooltip content={tooltipContent}>
                      <div
                        className={classes.infoDetail}
                        onClick={handleToggleDrawer}
                      >
                        All Reasons Call Needs Follow Up (
                        {_followUpReason.records.length})
                      </div>
                    </CustomTooltip>
                  </div>
                </div>
              </div>
              {firstRecordDate.length ? (
                <div className={classes.disclaimer}>
                  <InfoOutlinedIcon className={classes.disclaimerIcon} />
                  The data used in this graph is derived from the calls we
                  scored since {moment(firstRecordDate).format(dateFormat)}.
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <EmptyBarChart />
        )}
      </ChartCard>
      <CallDetailsDrawer
        drawerOpen={drawerOpen}
        handleCloseDrawer={handleToggleDrawer}
        title="All Reasons Call Needs Follow Up"
      >
        <AllCallNeedsFollowUpDrawer />
      </CallDetailsDrawer>
    </>
  )
}

export default observer(CallNeedsFollowUp)
